import { FirebaseError } from 'firebase/app';
import { PixelMark } from '../../shared/pixel';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { auth, googleProvider } from '../../shared/firebase/client';
import { signInWithFallback } from './signIn';
import { useAuth } from './useAuth';

interface LocationState {
  from?: { pathname: string };
}

/** แปลง error code ของ Firebase เป็นข้อความที่ user อ่านแล้วรู้ว่าต้องทำอะไรต่อ */
export function describeAuthError(error: unknown): string {
  if (!(error instanceof FirebaseError)) {
    return 'เข้าสู่ระบบไม่สำเร็จ ลองใหม่อีกครั้ง';
  }

  switch (error.code) {
    case 'auth/network-request-failed':
      return 'เชื่อมต่อเครือข่ายไม่ได้ ตรวจสอบอินเทอร์เน็ตแล้วลองใหม่';
    case 'auth/unauthorized-domain':
      return 'โดเมนนี้ยังไม่ได้รับอนุญาตใน Firebase Console → Authentication → Settings';
    case 'auth/user-disabled':
      return 'บัญชีนี้ถูกระงับการใช้งาน';
    default:
      return `เข้าสู่ระบบไม่สำเร็จ (${error.code})`;
  }
}

export function LoginPage() {
  const { user, isLoading, redirectError } = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
  // error จาก redirect เกิดก่อนที่หน้านี้จะ render จึงไม่ผ่าน setError — แสดงควบคู่กัน
  const visibleError = error ?? (redirectError !== null ? describeAuthError(redirectError) : null);

  // login อยู่แล้วก็ไม่ต้องเห็นหน้านี้
  if (!isLoading && user !== null) {
    return <Navigate to={from} replace />;
  }

  async function handleSignIn() {
    setError(null);
    setIsSigningIn(true);

    const outcome = await signInWithFallback(auth, googleProvider);

    switch (outcome.status) {
      case 'redirecting':
        // เบราว์เซอร์กำลังออกจากหน้านี้ — คงปุ่มเป็น disabled ไว้ ไม่ต้องคืนสถานะ
        return;
      case 'failed':
        setError(describeAuthError(outcome.error));
        break;
      case 'signed-in':
      case 'cancelled':
        // สำเร็จ → route guard พาไปเอง / ผู้ใช้กดปิดเอง → ไม่ต้องขึ้นเตือนอะไร
        break;
    }

    setIsSigningIn(false);
  }

  return (
    <main className="flex min-h-dvh items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        <div className="flex items-center justify-center gap-3">
          <PixelMark size={30} />
          <h1 className="font-pixel text-[17px] tracking-[0.5px] text-ink-100">PORTTANG</h1>
        </div>
        <p className="mt-4 text-sm text-ink-400">รวมพอร์ตลงทุนจากหลายแพลตฟอร์มมาดูที่เดียว</p>

        <button
          type="button"
          onClick={() => void handleSignIn()}
          disabled={isSigningIn || isLoading}
          className="mt-10 flex w-full items-center justify-center gap-3 rounded-lg bg-brand px-4 py-3 font-medium text-ink-100 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSigningIn ? 'กำลังเข้าสู่ระบบ…' : 'เข้าสู่ระบบด้วย Google'}
        </button>

        {visibleError !== null && (
          <p role="alert" className="mt-4 text-sm text-down">
            {visibleError}
          </p>
        )}

        <p className="mt-8 font-mono text-[11px] text-ink-600">
          เข้าได้เฉพาะบัญชีที่อยู่ในรายชื่อที่อนุญาต
        </p>
      </div>
    </main>
  );
}
